export type ComposerAttachmentKind = 'image' | 'video' | 'pdf';

export interface AttachmentTypeRule {
    mimeType: string;
    label: string;
    kind: ComposerAttachmentKind;
    extensions: readonly string[];
}

// Must match the backend's sendable DM attachment types and upload limit.
export const ATTACHMENT_TYPE_RULES: readonly AttachmentTypeRule[] = [
    { mimeType: 'image/jpeg', label: 'JPEG', kind: 'image', extensions: ['jpg', 'jpeg', 'jfif'] },
    { mimeType: 'image/png', label: 'PNG', kind: 'image', extensions: ['png'] },
    { mimeType: 'image/webp', label: 'WebP', kind: 'image', extensions: ['webp'] },
    { mimeType: 'video/mp4', label: 'MP4', kind: 'video', extensions: ['mp4'] },
    { mimeType: 'application/pdf', label: 'PDF', kind: 'pdf', extensions: ['pdf'] }
];

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export const ALLOWED_TYPES_SENTENCE = 'a JPEG, PNG or WebP image, an MP4 video or a PDF';

export const ATTACHMENT_ACCEPT = ATTACHMENT_TYPE_RULES.flatMap((rule) => [
    rule.mimeType,
    ...rule.extensions.map((extension) => `.${extension}`)
]).join(',');

const KB = 1024;
const MB = KB * 1024;

export function normalizeMimeType(mimeType: string): string {
    return mimeType.split(';')[0].trim().toLowerCase();
}

export function findAttachmentRule(mimeType: string): AttachmentTypeRule | undefined {
    const normalized = normalizeMimeType(mimeType);
    return ATTACHMENT_TYPE_RULES.find((rule) => rule.mimeType === normalized);
}

// For files the browser reports without a type; the backend still checks the bytes.
export function findAttachmentRuleByExtension(extension: string): AttachmentTypeRule | undefined {
    const normalized = extension.toLowerCase();
    return ATTACHMENT_TYPE_RULES.find((rule) => rule.extensions.includes(normalized));
}

export function formatFileSize(bytes: number): string {
    if (bytes < KB) return `${bytes} B`;
    if (bytes < MB) return `${Math.round(bytes / KB)} KB`;
    const megabytes = Math.round((bytes / MB) * 10) / 10;
    return `${megabytes} MB`;
}

export const MAX_ATTACHMENT_SIZE_LABEL = formatFileSize(MAX_ATTACHMENT_BYTES);
